import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { PhotoReelContent } from 'features/kestrelViewer/ui/tools/photoreel/carousel/PhotoReelContent'
import { fetchNearbyPhotos } from 'features/kestrelViewer/utils/fetchNearbyPhotos'
import omit from 'lodash/omit'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { KVAnnotation } from 'types'

const LoadingText = styled.div`
  font-size: 0.65rem;
  opacity: 0.75;
  letter-spacing: 0.05rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`

export const PhotoSelector: React.FC<{
  editedAnnotation: KVAnnotation
  setEditedAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
}> = ({ editedAnnotation, setEditedAnnotation }) => {
  const {
    kestrelModel,
    photos,
    filteredPhotos,
    setFilteredPhotos,
    filteringPhotos,
    setFilteringPhotos,
    setPhotos,
    setSelectedPhoto,
  } = useKestrelViewerContext()

  // Mirror photos into a ref so the effect doesn't depend on `photos`
  const photosRef = useRef(photos)
  useEffect(() => {
    photosRef.current = photos
  }, [photos])

  useEffect(() => {
    const run = async () => {
      const worldPosition = editedAnnotation.centeredPosition
      if (!worldPosition) {
        setFilteredPhotos(null)
        return
      }

      if (!kestrelModel?.id) {
        console.warn('No kestrel model available for nearby photos API')
        return
      }

      const cameraPoses = kestrelModel?.data?.cameraPoses
      if (!cameraPoses || cameraPoses.length === 0) {
        console.warn('No camera poses available for nearby photos')
        setFilteredPhotos(null)
        return
      }

      try {
        setFilteringPhotos(true)
        const photoResponses = await fetchNearbyPhotos(
          worldPosition,
          cameraPoses
        )

        const updatedPhotos = photoResponses
          .map((photoResponse) => {
            const existingPhoto = photosRef.current.find(
              (photo) => photo.filename === photoResponse.image_name
            )
            if (existingPhoto) {
              return {
                ...existingPhoto,
                projection: {
                  filename: photoResponse.image_name,
                  x: photoResponse.projection.x,
                  y: photoResponse.projection.y,
                  originalImageWidth: photoResponse.width,
                  originalImageHeight: photoResponse.height,
                },
              }
            }
            return null
          })
          .filter((photo): photo is NonNullable<typeof photo> => photo !== null)

        const photosWithoutProjections = photosRef.current
          .filter(
            (photo) =>
              !photoResponses.some((pr) => pr.image_name === photo.filename)
          )
          .map((photo) => omit(photo, 'projection'))

        const allPhotos = [...updatedPhotos, ...photosWithoutProjections]
        setPhotos(allPhotos)

        const filtered = updatedPhotos.length > 0 ? updatedPhotos : null
        setFilteredPhotos(filtered)
      } catch (error) {
        console.error(
          'Failed to fetch nearby photos for annotation position:',
          error
        )
        setFilteredPhotos(null)
        const clearedPhotos = photosRef.current.map((photo) =>
          omit(photo, 'projection')
        )
        setPhotos(clearedPhotos)
      } finally {
        setFilteringPhotos(false)
      }
    }

    void run()
  }, [
    editedAnnotation.centeredPosition,
    kestrelModel,
    setPhotos,
    setFilteredPhotos,
    setFilteringPhotos,
  ])

  // If no photos are selected, select the first photo
  useEffect(() => {
    if (
      filteredPhotos &&
      filteredPhotos.length > 0 &&
      editedAnnotation.imageNames?.length === 0
    ) {
      setEditedAnnotation((prev) => ({
        ...prev,
        imageNames: [filteredPhotos[0].id],
      }))
    }
  }, [filteredPhotos, editedAnnotation.imageNames, setEditedAnnotation])

  const onPhotoClick = (imageId: string) => {
    setEditedAnnotation((prev) => {
      // If image is already in the list, remove it
      if (prev.imageNames?.includes(imageId)) {
        return {
          ...prev,
          imageNames: prev.imageNames.filter((id) => id !== imageId),
        }
      } else {
        return {
          ...prev,
          imageNames: [...(prev.imageNames || []), imageId],
        }
      }
    })
  }

  const onPreviewClick = (imageId: string) => {
    const source = filteredPhotos || photos
    const photo = source.find((p) => p.id === imageId)
    if (photo) {
      setSelectedPhoto(photo)
    }
  }

  // If finished loading and there are no filtered photos, render nothing
  if (!filteredPhotos || filteredPhotos.length === 0) {
    return null
  }

  return (
    <div>
      {filteringPhotos ? (
        <LoadingText>Loading photos...</LoadingText>
      ) : (
        <PhotoReelContent
          photos={filteredPhotos || []}
          isLoading={filteringPhotos}
          error={null}
          onPhotoClick={onPhotoClick}
          highlightedImages={editedAnnotation.imageNames || []}
          onPreviewClick={onPreviewClick}
        />
      )}
    </div>
  )
}
